import type { ResidenceData, ResidenceValidationError } from '@repo/sdk/gen'
import { describeCharacter } from '@repo/ui/utils'
import type { FieldErrors, Path } from 'react-hook-form'
import { z } from 'zod'
import i18n, { type TKey } from '@/lib/i18n'

// Exhaustive over the API's codes: a new one does not compile until it has its message.
const MESSAGE_KEY: Record<ResidenceValidationError['code'], TKey> = {
	no_seats: 'admin-residences:errors.no_seats',
	rate_not_positive: 'admin-residences:errors.rate_not_positive',
	rate_too_low: 'admin-residences:errors.rate_too_low',
	sites_count_invalid: 'admin-residences:errors.sites_count_invalid',
	sites_beds_mismatch: 'admin-residences:errors.sites_beds_mismatch',
	site_equipped_above_beds:
		'admin-residences:errors.site_equipped_above_beds',
	equipped_below_minimum: 'admin-residences:errors.equipped_below_minimum',
	funded_above_total: 'admin-residences:errors.funded_above_total',
	q30_below_minimum: 'admin-residences:errors.q30_below_minimum',
	rate_required: 'admin-residences:errors.rate_required',
	double_beds_odd: 'admin-residences:errors.double_beds_odd',
	site_double_beds_odd: 'admin-residences:errors.site_double_beds_odd',
	iban_invalid: 'admin-residences:errors.iban_invalid',
	link_not_https: 'admin-residences:errors.link_not_https',
	duplicate_entries: 'admin-residences:errors.duplicate_entries',
	institution_unknown: 'admin-residences:errors.institution_unknown',
	required: 'admin-residences:errors.required',
	site_typology_required: 'admin-residences:errors.site_typology_required',
	site_bathrooms_required: 'admin-residences:errors.site_bathrooms_required',
	site_kitchens_required: 'admin-residences:errors.site_kitchens_required'
}

/**
 * The per-field problems of a `residence_invalid` answer, each with its
 * message; null for any other error. The thrown value is the parsed API error
 * body. The problems of a site land on the count at fault, in the site's own
 * section, whose values are the ones just sent.
 */
export function residenceErrorsOf(
	error: unknown,
	sites: ResidenceData['sites']
) {
	if (typeof error !== 'object' || error === null || !('code' in error)) {
		return null
	}
	if (
		error.code === 'residence_invalid' &&
		'errors' in error &&
		Array.isArray(error.errors)
	) {
		return groupProblems(
			error.errors as ResidenceValidationError[],
			(item) => fieldOf(item, sites)
		)
	}
	return null
}

const PrintableAnswerSchema = z.object({
	code: z.literal('text_not_printable'),
	fields: z.array(
		z.object({ field: z.string(), characters: z.array(z.string()) })
	)
})

/**
 * The fields of a `text_not_printable` answer, each with the characters it
 * holds, as the form shows them; null for any other error. The API names a
 * site as `sites[n]`, counted from one: the form counts from zero.
 */
export function printableErrorsOf(error: unknown) {
	const answer = PrintableAnswerSchema.safeParse(error)
	if (!answer.success) {
		return null
	}
	return answer.data.fields.map(({ field, characters }) => ({
		path: field
			.replace(
				/\[(\d+)\]/g,
				(_, index: string) => `.${Number(index) - 1}`
			)
			.split('.'),
		message: i18n.t('admin-residences:errors.text_not_printable', {
			characters: characters.map(describeCharacter).join(', ')
		})
	}))
}

/**
 * Each field with its problems said in one message: those of a refused save,
 * or the `issues` a residence still has before its call can be published.
 */
export function residenceProblemsOf(items: ResidenceValidationError[]) {
	return groupProblems(items, (item) => item.field)
}

/**
 * The input a problem is about. The API names the list of sites; the count at
 * fault inside the site it names is found in the values that were sent.
 */
function fieldOf(
	item: ResidenceValidationError,
	sites: ResidenceData['sites']
): Path<ResidenceData> {
	const index = (item.params?.site ?? 0) - 1
	const site = sites[index]
	if (item.field !== 'sites' || !site) {
		return item.field
	}
	if (item.code === 'site_double_beds_odd') {
		return site.doubleBeds % 2 === 0
			? `sites.${index}.equippedDoubleBeds`
			: `sites.${index}.doubleBeds`
	}
	if (item.code === 'site_equipped_above_beds') {
		return site.equippedSingleBeds > site.singleBeds
			? `sites.${index}.equippedSingleBeds`
			: `sites.${index}.equippedDoubleBeds`
	}
	return item.field
}

function groupProblems<Field extends string>(
	items: ResidenceValidationError[],
	fieldOf: (item: ResidenceValidationError) => Field
) {
	// A field can have several problems (the list of sites does): one message says them all.
	const byField = new Map<Field, string[]>()
	for (const item of items) {
		const field = fieldOf(item)
		byField.set(field, [
			...(byField.get(field) ?? []),
			i18n.t(MESSAGE_KEY[item.code], {
				minimum: item.params?.minimum,
				total: item.params?.total,
				site: item.params?.site,
				expectedSingle: item.params?.expectedSingle,
				expectedDouble: item.params?.expectedDouble,
				single: item.params?.single,
				double: item.params?.double,
				equipped: item.params?.equipped,
				expected: euro(item.params?.expected),
				maximum: euro(item.params?.maximum)
			})
		])
	}
	return [...byField].map(([field, messages]) => ({
		field,
		message: messages.join(' ')
	}))
}

/**
 * Every message the form holds, each with the path of its field, as the
 * resolver and the API left them. A list of sites holds a message of its own
 * besides those of its sites.
 */
export function messagesOf(
	errors: FieldErrors<ResidenceData>
): { path: string[]; message: string }[] {
	const walk = (
		node: unknown,
		path: string[]
	): { path: string[]; message: string }[] => {
		if (typeof node !== 'object' || node === null) {
			return []
		}
		const own =
			'message' in node &&
			typeof node.message === 'string' &&
			node.message
				? [{ path, message: node.message }]
				: []
		const inside = Object.entries(node)
			.filter(([key]) => !SKIPPED_KEYS.has(key))
			.flatMap(([key, child]) =>
				walk(child, key === 'root' ? path : [...path, key])
			)
		return [...own, ...inside]
	}
	return walk(errors, [])
}

const SKIPPED_KEYS = new Set(['message', 'type', 'types', 'ref'])

/** Euro cents in the reader's format: "191,30 €". */
export function euro(cents: number | null | undefined) {
	if (cents === null || cents === undefined) {
		return ''
	}
	return new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: 'EUR'
	}).format(cents / 100)
}
